import { t } from 'i18next';
import { features } from '../../constants/features';

type Coupon = {
  code: string;
};

type RequirementType = 'coupons' | 'spendAmount' | 'numItems' | 'maxUsesPerCustomer';

type OtherRequirementType = 'productsTotalItems' | 'productsTotalQuantity';

export type OtherRequirement = {
  requirement_type: RequirementType;
  coupons: Coupon[];
  tax_type: string;
  min: number | null;
  max: number | null;
  type: OtherRequirementType;
  number: number | null;
};

type RewardType =
  | 'product'
  | 'order'
  | 'shipping'
  | 'cart|productSKU'
  | 'cart|cheapestProductSKU'
  | 'cart|productMetadata'
  | 'cart|cheapestProductMetadata'
  | 'requirements|all'
  | 'requirements|cheapest';

type RewardAmountType = 'percent' | 'priceAllNet' | 'fixedAllNet' | 'none';

type RestrictionType = 'equals' | 'notEquals' | 'contains' | 'notContains';

export type Reward = {
  type: RewardType;
  amount_type: RewardAmountType;
  item_id: string;
  metadata_namespace?: string;
  metadata_key?: string;
  restriction_type?: RestrictionType;
  amount: number;
  id?: string;
};

type Restriction = {
  type: string;
  ref: string;
  metadata_namespace?: string;
  metadata_key?: string;
};

type Item = {
  id?: string;
  restrictions: Restriction[];
  points_type: string;
  points: number;
  quantity: number;
  selection_type: string;
};

type Requirement = {
  items: Array<Item>;
  max_rewards: number;
  min_spend: number | null;
  max_spend: number | null;
  spend_tax_type: string;
  selection_type: string;
  min_points: number;
};

export type Discount = {
  id: string;
  template?: string;
  name: string;
  internal_name: string;
  internal_slug: string;
  calculation_group: number;
  priority: number;
  simultaneity: string[] | null;
  times_applicable_per_order: number;
  groups: string[];
  requirement_selection_type: string;
  reward_selection_type: string;
  currency_code: string | null;
  start_date: string | null;
  end_date: string | null;
  active: boolean;
  other_requirements: Partial<OtherRequirement>[];
  requirements: Requirement[];
  max_rewards: number | null;
  rewards: Reward[];
  excluded_customers: string[];
};

export type PartialDiscount = Partial<Discount>;

export type DiscountTypeMetaID =
  | 'order_level_get_amount_off'
  | 'order_level_get_free_product'
  | 'product_level_get_amount_off'
  | 'order_level_get_shipping_reward'
  | 'advanced_order_level_get_amount_off'
  | 'order_level_requirement_amount_off'
  | 'advanced_discount'
  | 'advanced_free_products';

export type DiscountTypeMeta = {
  id: DiscountTypeMetaID;
  label: string;
  phaseFeatureFlag: string | null;
  planFeatureFlag: string | null;
  onlyInternalShipping?: boolean;
  showInDiscountTypeSelector?: boolean;
};

export const discountTypes: Record<DiscountTypeMetaID, DiscountTypeMeta> = {
  order_level_get_amount_off: {
    id: 'order_level_get_amount_off',
    label: t('features:discounts.components.list_table.filters.types.order_level_get_amount_off'),
    phaseFeatureFlag: null,
    planFeatureFlag: null, // Starter – anyone can use
    showInDiscountTypeSelector: true,
  },
  order_level_get_free_product: {
    id: 'order_level_get_free_product',
    label: t('features:discounts.components.list_table.filters.types.order_level_get_free_product'),
    phaseFeatureFlag: null,
    planFeatureFlag: features.DISCOUNTS_FREE_PRODUCT,
    showInDiscountTypeSelector: true,
  },
  product_level_get_amount_off: {
    id: 'product_level_get_amount_off',
    label: t('features:discounts.components.list_table.filters.types.product_level_get_amount_off'),
    phaseFeatureFlag: null,
    planFeatureFlag: features.DISCOUNTS_PRODUCT_LEVEL_AMOUNT_OFF,
    showInDiscountTypeSelector: true,
  },
  order_level_get_shipping_reward: {
    id: 'order_level_get_shipping_reward',
    label: t(
      'features:discounts.components.list_table.filters.types.order_level_get_shipping_reward',
    ),
    phaseFeatureFlag: null,
    planFeatureFlag: features.DISCOUNTS_SHIPPING_REWARD,
    onlyInternalShipping: true,
    showInDiscountTypeSelector: true,
  },
  advanced_order_level_get_amount_off: {
    id: 'advanced_order_level_get_amount_off',
    label: t(
      'features:discounts.components.list_table.filters.types.advanced_order_level_get_amount_off',
    ),
    phaseFeatureFlag: null,
    planFeatureFlag: features.DISCOUNTS_ADVANCED,
    showInDiscountTypeSelector: false,
  },
  order_level_requirement_amount_off: {
    id: 'order_level_requirement_amount_off',
    label: t(
      'features:discounts.components.list_table.filters.types.order_level_requirement_amount_off',
    ),
    phaseFeatureFlag: null,
    planFeatureFlag: features.DISCOUNTS_ADVANCED,
    showInDiscountTypeSelector: false,
  },
  advanced_discount: {
    id: 'advanced_discount',
    label: t('features:discounts.components.list_table.filters.types.advanced_discount'),
    phaseFeatureFlag: null,
    planFeatureFlag: features.DISCOUNTS_ADVANCED,
    showInDiscountTypeSelector: true,
  },
  advanced_free_products: {
    id: 'advanced_free_products',
    label: t('features:discounts.components.list_table.filters.types.advanced_free_products'),
    phaseFeatureFlag: null,
    planFeatureFlag: features.DISCOUNTS_ADVANCED,
    showInDiscountTypeSelector: true,
  },
} as const;
